import {
  CheckCircle2,
  Clock,
  FileEdit,
  Loader2,
  Rocket,
  Search,
  Sparkles,
} from 'lucide-react';
import ApprovalDesigner from './ApprovalDesigner';
import BpmSimulatePanel from './BpmSimulatePanel';
import BpmConditionEditor from './BpmConditionEditor';
import BpmTransactionAttributesPanel from './BpmTransactionAttributesPanel';

const STEPS = [
  { id: 'choose', label: 'Choose transaction', Icon: Search },
  { id: 'define', label: 'Write the rule', Icon: FileEdit },
  { id: 'review', label: 'Review & simulate', Icon: CheckCircle2 },
  { id: 'deploy', label: 'Deploy', Icon: Rocket },
];

const TRANSACTIONS = [
  {
    id: 'absence',
    title: 'Absence',
    hint: 'Leave, sick days, and time-off approvals',
    starters: [
      'If absence days > 5, route to Manager then HR, else auto-approve.',
      'Sick leave over 3 days needs manager approval.',
    ],
  },
  {
    id: 'time_labor',
    title: 'Time & Labor',
    hint: 'Timecards, overtime, and hours approvals',
    starters: [
      'Overtime over 10 hours routes to the 2nd-level manager, else auto-approve.',
      'Timecards with comments require supervisor review.',
    ],
  },
];

function Stepper({ step, unlockedThrough, onSelect }) {
  return (
    <nav aria-label="Approval steps" className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
      {STEPS.map(({ id, label, Icon }, i) => {
        const active = i === step;
        const done = i < step;
        const locked = i > unlockedThrough;
        return (
          <div key={id} className="flex items-center gap-1">
            {i > 0 ? (
              <span className="mx-0.5 hidden text-slate-300 sm:inline" aria-hidden>
                →
              </span>
            ) : null}
            <button
              type="button"
              aria-disabled={locked}
              tabIndex={locked ? -1 : 0}
              onClick={() => {
                if (!locked) onSelect(i);
              }}
              className={`orca-stepper-pill inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                active
                  ? 'bg-orca-accent orca-on-accent'
                  : done
                    ? 'bg-orca-raised text-orca-accent'
                    : 'bg-orca-raised text-orca-strong'
              } ${locked ? 'cursor-not-allowed' : 'hover:brightness-95'}`}
              aria-current={active ? 'step' : undefined}
            >
              <Icon className="h-3 w-3 shrink-0" aria-hidden />
              <span className="hidden sm:inline">{label}</span>
              <span className="sm:hidden">{i + 1}</span>
            </button>
          </div>
        );
      })}
    </nav>
  );
}

export default function ApprovalCopilotLanding({
  step,
  onStepChange,
  txnPicked,
  category,
  onPickTransaction,
  connected,
  tenantHost,
  onOpenDesigner,
  onOpenConnections,
  policy,
  onPolicyChange,
  onGenerate,
  generating,
  genSeconds,
  notice,
  generateOk,
  error,
  api,
  design,
  designForPush,
  designResult,
  onDesignChange,
  onSimulateResult,
  onPathSimulateResult,
  onDeployResult,
  onSimulate,
  simulateLoading,
  simulateResult,
  onConditionChange,
  designerRef,
  pushEnabled,
  canPush,
  pushTargets,
  pushTargetId,
  onPushTargetChange,
  onPush,
  pushLoading,
  pushResult,
}) {
  const txn = TRANSACTIONS.find((t) => t.id === category) || TRANSACTIONS[0];
  const unlockedThrough = !txnPicked ? 0 : design?.stages?.length ? 3 : 1;
  const hasDesign = Boolean(design?.stages?.length);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-orca bg-orca-panel px-3 py-1.5">
        <Stepper step={step} unlockedThrough={unlockedThrough} onSelect={onStepChange} />
        <div className="flex shrink-0 items-center gap-2">
          {connected ? (
            <span className="hidden max-w-[14rem] truncate text-[11px] text-orca-muted sm:inline" title={tenantHost}>
              {tenantHost || 'Fusion connected'}
            </span>
          ) : (
            <button
              type="button"
              onClick={onOpenConnections}
              className="text-[11px] font-medium text-amber-600 hover:underline"
            >
              Connect Fusion
            </button>
          )}
          <button
            type="button"
            onClick={onOpenDesigner}
            className="text-[11px] font-medium text-orca-accent hover:opacity-80"
          >
            Advanced
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-orca-base px-3 py-3">
        {error ? (
          <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            {error}
          </div>
        ) : null}

        {step === 0 && (
          <div className="mx-auto max-w-xl space-y-3">
            <h3 className="text-sm font-semibold text-orca-strong">What approval are you changing?</h3>
            <p className="text-xs text-orca-muted">Pick one, then describe the rule in plain English.</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {TRANSACTIONS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onPickTransaction(t.id)}
                  className={`rounded-xl border px-4 py-3 text-left ${
                    txnPicked && category === t.id
                      ? 'border-teal-600 bg-orca-panel'
                      : 'border-orca bg-orca-panel hover:border-teal-600'
                  }`}
                >
                  <div className="text-sm font-semibold text-orca-strong">{t.title}</div>
                  <div className="mt-1 text-xs text-orca-muted">{t.hint}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="mx-auto max-w-2xl space-y-3">
            <p className="text-xs text-orca-muted">
              Using <span className="font-semibold text-orca-strong">{txn.title}</span> approvals.
            </p>
            <textarea
              value={policy}
              onChange={(e) => onPolicyChange(e.target.value)}
              rows={6}
              disabled={generating}
              placeholder={`e.g. ${txn.starters[0]}`}
              className="min-h-[9rem] w-full resize-y rounded-lg border border-orca bg-orca-panel px-3 py-2.5 text-sm text-orca-strong placeholder:text-orca-muted focus:border-teal-600 focus:outline-none"
            />
            <div className="flex flex-wrap gap-1.5">
              {txn.starters.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onPolicyChange(s)}
                  className="rounded-md border border-orca bg-orca-raised px-2.5 py-1 text-left text-[11px] text-orca-strong hover:border-teal-600"
                >
                  {s}
                </button>
              ))}
            </div>
            {notice ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                {notice}
              </div>
            ) : null}
            {generating ? (
              <div className="flex items-center gap-2 text-xs text-orca-muted">
                <Loader2 className="h-4 w-4 animate-spin text-orca-accent" />
                Generating your rule… {genSeconds}s
              </div>
            ) : null}
            {generateOk && hasDesign ? (
              <div className="flex items-center gap-2 text-xs text-emerald-700">
                <CheckCircle2 className="h-4 w-4" />
                Rule ready — review it on the next step.
              </div>
            ) : null}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onGenerate}
                disabled={generating || !policy.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-orca-accent orca-on-accent px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-40"
              >
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Generate
              </button>
              {hasDesign ? (
                <button
                  type="button"
                  onClick={() => onStepChange(2)}
                  className="text-sm font-medium text-orca-accent hover:opacity-80"
                >
                  Review →
                </button>
              ) : null}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            {!hasDesign ? (
              <p className="text-sm text-orca-muted">Generate a rule first, then review the path here.</p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={onSimulate}
                    disabled={simulateLoading}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-orca-accent orca-on-accent px-3 py-1.5 text-xs font-semibold hover:opacity-90 disabled:opacity-40"
                  >
                    {simulateLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clock className="h-3.5 w-3.5" />}
                    Simulate
                  </button>
                  <button
                    type="button"
                    onClick={() => onStepChange(3)}
                    className="text-xs font-medium text-orca-accent hover:opacity-80"
                  >
                    Deploy →
                  </button>
                </div>
                {simulateResult?.ok ? (
                  <div className="rounded-lg border border-orca bg-orca-panel px-3 py-2 text-xs text-orca-strong">
                    <p className="font-medium">{simulateResult.summary}</p>
                  </div>
                ) : null}
                <div ref={designerRef} className="min-h-[420px]">
                  <ApprovalDesigner
                    api={api}
                    externalDesign={design}
                    externalDesignLabel={policy.trim() || 'Generated rule'}
                    externalPolicyText={policy}
                    transactionCategory={category}
                    onDesignChange={onDesignChange}
                    onSimulateResult={onSimulateResult}
                    onDeployResult={onDeployResult}
                  />
                </div>
                <BpmSimulatePanel
                  design={designForPush}
                  api={api}
                  onResult={onPathSimulateResult}
                  disabled={generating}
                />
                <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(220px,280px)]">
                  <BpmConditionEditor
                    condition={
                      Array.isArray(design.conditions) && design.conditions[0]
                        ? design.conditions[0]
                        : {}
                    }
                    onChange={onConditionChange}
                    disabled={generating}
                    transactionCategory={category}
                  />
                  <BpmTransactionAttributesPanel
                    api={api}
                    category={category}
                    disabled={generating}
                    onPickAttribute={onConditionChange}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="mx-auto max-w-xl space-y-3 rounded-xl border border-orca bg-orca-panel p-4">
            <h3 className="text-sm font-semibold text-orca-strong">Deploy to Fusion</h3>
            {!hasDesign ? (
              <p className="text-sm text-orca-muted">Generate and review a rule first.</p>
            ) : (
              <>
                {!pushEnabled ? (
                  <p className="text-xs text-orca-muted">
                    Deploy is off on this server. Download the rule from Review, or ask an admin to enable push.
                  </p>
                ) : null}
                <p className="text-xs text-orca-muted">
                  Category: <span className="font-medium text-orca-strong">{txn.title}</span>
                </p>
                {pushTargets.length > 0 ? (
                  <label className="block text-xs text-orca-muted">
                    Connection
                    <select
                      value={pushTargetId}
                      onChange={(e) => onPushTargetChange(e.target.value)}
                      className="mt-1 w-full rounded-md border border-orca bg-orca-raised px-2 py-1.5 text-sm text-orca-strong"
                    >
                      {pushTargets.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                          {t.isActive ? ' (active)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onPush(true)}
                    disabled={!canPush || pushLoading}
                    className="rounded-lg border border-orca bg-orca-raised px-3 py-1.5 text-xs font-medium text-orca-strong disabled:opacity-40"
                  >
                    {pushLoading ? 'Working…' : 'Dry-run'}
                  </button>
                  <button
                    type="button"
                    onClick={() => onPush(false)}
                    disabled={!canPush || pushLoading}
                    className="rounded-lg bg-orca-accent orca-on-accent px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
                  >
                    {pushLoading ? 'Working…' : 'Push to Fusion'}
                  </button>
                </div>
                {pushResult ? (
                  <p
                    className={`text-xs ${
                      pushResult.ok === false ? 'text-rose-700' : 'text-emerald-700'
                    }`}
                  >
                    {pushResult.summary || pushResult.error || 'Done'}
                  </p>
                ) : null}
                {designResult?.warnings?.length ? (
                  <ul className="list-disc pl-4 text-[11px] text-amber-800">
                    {designResult.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                ) : null}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
